import React, { useMemo } from 'react';
import ReactApexChart from 'react-apexcharts';
import type { ApexOptions } from 'apexcharts';

type PriseItem = {
    quantite?: number;
};

type PrisesType = Record<
    string,
    {
        gasoil?: PriseItem;
        essence?: PriseItem;
        autres?: PriseItem;
    }
>;

const PrisesBarChart: React.FC<{ prises?: PrisesType; height?: number }> = ({ prises = {}, height = 360 }) => {
    const months = useMemo(() => {
        if (!prises || Object.keys(prises).length === 0) return [];
        // Tri simple pour une ordre stable (ajuste si tes clés ne sont pas triables lexicographiquement)
        return Object.keys(prises);
    }, [prises]);

    const series = useMemo(() => {
        if (!months || months.length === 0) return [];

        const gasoil = months.map((m) => prises[m]?.gasoil?.quantite ?? 0);
        const essence = months.map((m) => prises[m]?.essence?.quantite ?? 0);
        const autres = months.map((m) => prises[m]?.autres?.quantite ?? 0);

        return [
            { name: 'Gasoil', data: gasoil },
            { name: 'Essence', data: essence },
            { name: 'Autres', data: autres },
        ];
    }, [prises, months]);

    const options: ApexOptions = useMemo(() => {
        return {
            chart: {
                type: 'bar',
                stacked: false,
                zoom: { enabled: false },
                toolbar: { show: false },
            },
            colors: ['#00A65A', '#FFD700', '#888888'],
            plotOptions: {
                bar: {
                    horizontal: false,
                    columnWidth: '50%',
                    endingShape: 'rounded',
                },
            },
            dataLabels: { enabled: false },
            stroke: {
                show: true,
                width: 1,
                colors: ['transparent'],
            },
            xaxis: {
                categories: months,
                title: { text: 'Mois' },
            },
            yaxis: {
                title: { text: 'Quantité' },
                labels: {
                    formatter: (val) => (Number.isInteger(val) ? String(val) : val.toFixed(2)),
                },
            },
            legend: {
                position: 'top',
                horizontalAlign: 'left',
                offsetY: 0,
            },
            tooltip: {
                y: {
                    formatter: (val) => `${val}`,
                },
            },
            grid: {
                borderColor: '#e7e7e7',
            },
            responsive: [
                {
                    breakpoint: 768,
                    options: {
                        plotOptions: {
                            bar: { columnWidth: '70%' },
                        },
                        legend: { position: 'bottom' },
                    },
                },
            ],
        } as ApexOptions;
    }, [months]);

    if (!months.length) {
        return <div className="alert alert-warning">Pas de données à afficher</div>;
    }

    return (
        <div>
            <ReactApexChart options={options} series={series} type="bar" height={height} />
        </div>
    );
};

export default PrisesBarChart;
